/*  MUI  */
import { Box, Card, CardContent, Grid, Typography } from '@mui/material';

/*  Shared  */
import { getDateTime } from 'application/Shared/Components/Core';
import { Helpers } from 'application/Shared/Helpers';

/* Hooks */
import { useFetchUsers } from 'hooks/dataAccess/User/user';

/*  Objects  */
import Alias from 'objects/Alias';
import { Browser } from 'objects/Browser';

/*  React  */
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

const getAliasValues = (user) => {
    if (!user?.aliasMap) {
        return { aliasMap: null, aliasOS: null };
    }

    const userAliasMap = { ...user.aliasMap };

    // Filter out the devices whose aliases are empty strings
    userAliasMap['3_device_name'] = userAliasMap['3_device_name'].filter(
        (item) => item !== ''
    );

    let filteredAliasOS;

    if (Helpers.isset(user, 'aliasOS')) {
        // If there are devices with "" aliases, there will be objects whose key is an empty string here, too.
        filteredAliasOS = Object.fromEntries(
            Object.entries(user.aliasOS).filter(([key]) => key !== '')
        );
    }

    return { aliasMap: userAliasMap, aliasOS: filteredAliasOS };
};

const LastCheckIn = ({ lastCheckIn }) => {
    const { t } = useTranslation();
    return (
        <Box display="flex" justifyContent="flex-end">
            {t('Common.userLastCheckIn', {
                date: getDateTime({ date: lastCheckIn, noTz: true })
            })}
        </Box>
    );
};
const Aliases = ({ aliasMap, aliasOS }) =>
    Object.keys(aliasMap).map((key) => (
        <Alias
            key={`alias_${key}`}
            orderedKey={key}
            aliases={aliasMap[key]}
            alias={aliasMap[key][0]}
            aliasOS={aliasOS}
        />
    ));

const Browsers = ({ browsers }) => {
    const render = [];

    browsers.forEach((b) => {
        render.push(<Browser browser={b} key={`browser_${b.id}`} />);
    });

    return render;
};

export const UserAliasesBrowsersCard = () => {
    const { t } = useTranslation();

    const { clientId, userId } = useParams();
    const params = useMemo(
        () => ({ id: userId, includeAliases: true, includeBrowsers: true }),
        [userId]
    );
    const { users } = useFetchUsers({
        clientId,
        params
    });

    const user = useMemo(() => users[0], [users]);
    const { aliasMap, aliasOS } = useMemo(() => getAliasValues(user), [user]);

    const lastCheckIn = user?.lastCheckIn;
    const browsers = user?.browsers;

    return (
        <Card
            variant="outlined"
            style={{ height: '100%', minHeight: '256px' }}
            data-testid="userAliasesBrowsersCard"
        >
            <CardContent>
                <Grid container spacing={3}>
                    <Grid item xs={6}>
                        <Typography
                            color="textSecondary"
                            variant="body2"
                            style={{ fontWeight: 600 }}
                        >
                            {t('Common.aliases')}
                        </Typography>
                        {aliasMap && (
                            <div style={{ marginTop: '8px' }}>
                                <Aliases
                                    aliasMap={aliasMap}
                                    aliasOS={aliasOS}
                                />
                            </div>
                        )}
                    </Grid>
                    <Grid item xs={6}>
                        <Typography
                            color="textSecondary"
                            variant="body2"
                            style={{ fontWeight: 600 }}
                        >
                            {t('Common.browsers')}
                        </Typography>
                        {Boolean(browsers?.length) && (
                            <div style={{ marginTop: '8px' }}>
                                <Browsers browsers={browsers} />
                            </div>
                        )}
                    </Grid>
                    {lastCheckIn && (
                        <Grid
                            item
                            xs={12}
                            data-testid="userAliasesBrowsersCardLastCheckIn"
                        >
                            <LastCheckIn lastCheckIn={lastCheckIn} />
                        </Grid>
                    )}
                </Grid>
            </CardContent>
        </Card>
    );
};
